"use client"

import { Box, Group, Text, Stack } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import type { OrderBook } from "@/lib/queries"

interface AggregatedLevel {
  price: number
  volume: number
  cumulative: number
}

function aggregateLevels(
  levels: Array<{ price: number; volume: number }>,
  precision: number
): AggregatedLevel[] {
  const buckets = new Map<number, number>()
  for (const l of levels) {
    const bucket = Math.round(Math.floor(l.price / precision) * precision * 100) / 100
    buckets.set(bucket, (buckets.get(bucket) ?? 0) + l.volume)
  }
  let cumulative = 0
  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([price, volume]) => {
      cumulative += volume
      return { price, volume, cumulative }
    })
}

interface AggregatedDepthChartProps {
  orderBook: OrderBook
  precision: number
}

export function AggregatedDepthChart({ orderBook, precision }: AggregatedDepthChartProps) {
  const t = useTranslations("trading")

  // Bids: descending (highest bid at top, nearest spread)
  const aggBids = aggregateLevels(orderBook.bids, precision).reverse()
  // Asks: descending (highest ask at top, best ask nearest spread)
  const aggAsks = aggregateLevels(orderBook.asks, precision).reverse()

  // Bar widths are proportional to individual level volume (not cumulative)
  const maxSingleVol = Math.max(
    ...aggBids.map((l) => l.volume),
    ...aggAsks.map((l) => l.volume),
    1
  )

  const bestBid = aggBids.at(-1)?.price  // lowest price in descending list = best bid nearest spread
  const bestAsk = aggAsks.at(-1)?.price  // lowest price in descending ask list = best ask
  const spread = bestBid != null && bestAsk != null ? (bestAsk - bestBid).toFixed(2) : null

  function depthRow(level: AggregatedLevel, side: "bid" | "ask") {
    const pct = (level.volume / maxSingleVol) * 100
    const bg = side === "bid" ? "green.2" : "red.2"
    const textColor = side === "bid" ? "green.7" : "red.7"
    return (
      <Group key={`${side}-${level.price}`} gap={4} wrap="nowrap" style={{ position: "relative" }}>
        <Box bg={bg} style={{ width: `${pct}%`, height: 18, minWidth: 2, borderRadius: 2 }} />
        <Text size="xs" c={textColor} fw={500} style={{ position: "absolute", left: 4 }}>
          €{level.price.toFixed(2)}
        </Text>
        <Text size="xs" c="dimmed" style={{ position: "absolute", right: 0 }}>
          {level.volume.toFixed(0)}
        </Text>
      </Group>
    )
  }

  return (
    <Stack gap={4}>
      {spread != null && (
        <Group gap={4}>
          <Text size="xs" c="dimmed">{t("marketTree.spread")}:</Text>
          <Text size="xs" fw={600}>€{spread}</Text>
        </Group>
      )}

      {/* Asks — red, sorted desc (highest ask at top, best ask nearest spread) */}
      <Stack gap={1}>
        {aggAsks.map((level) => depthRow(level, "ask"))}
      </Stack>

      {/* Spread gap */}
      {spread != null && (
        <Text size="xs" ta="center" c="dimmed" py={2}>
          ── {t("marketTree.spread")} €{spread} ──
        </Text>
      )}

      {/* Bids — green, sorted desc (best bid nearest spread, lower bids below) */}
      <Stack gap={1}>
        {aggBids.map((level) => depthRow(level, "bid"))}
      </Stack>
    </Stack>
  )
}
